import React, { useEffect, useState } from "react";
import { ActivityCard, ProgressPill } from "../shared";

/**
 * PartTapGame — "Find the Parts" sentence-splitting rounds.
 *
 * Each round shows a sentence word by word: first tap its مبتدأ, then its
 * خبر (any word of a multi-word خبر counts). Wrong taps flash and bounce;
 * a solved sentence plays in full. Completes when every round is solved.
 */
export default function PartTapGame({ data, onComplete, audio }) {
  const [roundIdx, setRoundIdx] = useState(0);
  const [stage, setStage] = useState("mubtada"); // mubtada | khabar | solved
  const [wrongIdx, setWrongIdx] = useState(null);
  const [solvedCount, setSolvedCount] = useState(0);

  const rounds = data.rounds;
  const round = rounds[roundIdx];
  const done = solvedCount >= rounds.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleTap = (idx) => {
    if (!round || stage === "solved") return;
    const target = stage === "mubtada" ? round.mubtada_indexes : round.khabar_indexes;
    if (target.includes(idx)) {
      if (stage === "mubtada") {
        setStage("khabar");
        audio.play(round.words.filter((_, i) => round.mubtada_indexes.includes(i)).join(" "));
      } else {
        setStage("solved");
        audio.play(round.words.join(" "));
        setTimeout(() => {
          setSolvedCount((n) => n + 1);
          if (roundIdx + 1 < rounds.length) {
            setRoundIdx((i) => i + 1);
            setStage("mubtada");
          }
        }, 1600);
      }
    } else {
      setWrongIdx(idx);
      setTimeout(() => setWrongIdx(null), 600);
    }
  };

  const wordStyle = (idx) => {
    const isMubtada = round.mubtada_indexes.includes(idx);
    const isKhabar = round.khabar_indexes.includes(idx);
    if (wrongIdx === idx) {
      return { background: "rgba(220,90,90,0.15)", border: "1px solid #dc5a5a", color: "#e58c8c" };
    }
    if (isMubtada && stage !== "mubtada") {
      return { background: "var(--gold-bg)", border: "1px solid var(--gold-dim)", color: "var(--gold-bright)" };
    }
    if (isKhabar && stage === "solved") {
      return { background: "var(--violet-bg)", border: "1px solid var(--tag-violet)", color: "var(--tag-violet)" };
    }
    return { background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment)" };
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-5">
        <ProgressPill>{solvedCount}/{rounds.length} split</ProgressPill>
      </div>

      {!done && round && (
        <>
          <p className="text-base text-center font-medium mb-1" style={{ color: "var(--parchment)" }}>
            {stage === "mubtada" && (
              <>Tap the <span dir="rtl" lang="ar" className="font-arabic" style={{ color: "var(--gold-bright)" }}>المُبْتَدَأ</span> — who or what it's about</>
            )}
            {stage === "khabar" && (
              <>Now tap the <span dir="rtl" lang="ar" className="font-arabic" style={{ color: "var(--tag-violet)" }}>الخَبَر</span> — what's said about it</>
            )}
            {stage === "solved" && <span style={{ color: "var(--teal)" }}>Split! ✓</span>}
          </p>
          <p className="text-sm text-center mb-5" style={{ color: "var(--parchment-dim)" }}>{round.en}</p>

          <div dir="rtl" className="flex flex-wrap justify-center gap-2.5">
            {round.words.map((word, idx) => (
              <button
                key={`${round.id}-${idx}`}
                type="button"
                lang="ar"
                onClick={() => handleTap(idx)}
                disabled={stage === "solved"}
                className={`font-arabic text-3xl sm:text-4xl leading-[1.8] px-4 pt-6 pb-2 rounded-xl transition-all hover:brightness-110 disabled:cursor-default ${wrongIdx === idx ? "animate-pulse" : ""}`}
                style={wordStyle(idx)}
              >
                {word}
              </button>
            ))}
          </div>
        </>
      )}

      {done && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}>
          <span className="text-lg leading-none" aria-hidden="true">🧩</span>
          <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
            All {rounds.length} split — you can spot the مُبْتَدَأ and the خَبَر in any sentence you meet.
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
